import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Radio, Input, Button, Spin, Select, notification } from "antd";
import { AuthContext } from "../AuthContext";
import { webAPIs } from "../config/webAPIs";
import { useNavigate, useLocation } from "react-router-dom";
import {
  SendOutlined,
  UserOutlined,
  RobotOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import axiosInstance from "../config/axiosInstance";
import TokenUsageIndicator from "../components/TokenUsageIndicator";

const QuickPromptPage = () => {
  const [loading, setLoading] = useState(true);
  const [presetPrompts, setPresetPrompts] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [openTextValues, setOpenTextValues] = useState({});
  const [outputLanguage, setOutputLanguage] = useState("English");
  const {
    user,
    loggedIn,
    isLoading,
    files,
    setFiles,
    setLoading: setGlobalLoading,
    checked,
    adminPrompt,
    systemRoles,
    selectedDocIds,
    setSelectedDocIds,
    selectedProjectId,
    message: globalMessage,
  } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [lastTokenInfo, setLastTokenInfo] = useState(null);
  const prevMessageCountRef = useRef(messages.length);

  const userRoleTitle = useMemo(() => {
    const userRole = systemRoles.find((role) => role.id === user?.systemRole);
    return userRole?.title || "Loading...";
  }, [systemRoles, user?.systemRole]);

  const fetchPreviousConversation = useCallback(async () => {
    if (user && selectedProjectId) {
      try {
        const response = await axiosInstance.get(
          `${webAPIs.getConversation}?projectId=${selectedProjectId}`
        );
        if (response.data.success && response.data.result.conversation) {
          setMessages(response.data.result.conversation);
        }
      } catch (error) {
        console.error("Error fetching conversation:", error);
        notification.error({
          description: "Failed to load previous conversations",
          duration: 2,
        });
      }
    }
  }, [user, selectedProjectId]);

  const fetchPresetPrompts = useCallback(async () => {
    try {
      const response = await axiosInstance.get(webAPIs.getPresetPrompts);
      if (response.data.success) {
        setPresetPrompts(response.data.data);
      }
    } catch (error) {
      notification.error({
        description:
          error.response?.data?.error || "Failed to fetch preset prompts",
        duration: 2,
      });
    }
  }, [user]);

  useEffect(() => {
    const loadInitialData = async () => {
      if (!loggedIn) {
        navigate("/");
        return;
      }

      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        await Promise.all([fetchPresetPrompts(), fetchPreviousConversation()]);
      } catch (error) {
        console.error("Error loading initial data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (!isLoading) {
      loadInitialData();
    }
  }, [
    isLoading,
    loggedIn,
    navigate,
    user,
    fetchPresetPrompts,
    fetchPreviousConversation,
  ]);

  useEffect(() => {
    const messagesContainer = document.querySelector(".messages-container");
    if (messagesContainer) {
      if (messages.length > prevMessageCountRef.current) {
        const userMessages =
          messagesContainer.querySelectorAll(".bg-userColor");
        if (userMessages.length > 0) {
          const lastUserMessage = userMessages[userMessages.length - 1];
          lastUserMessage.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        } else {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
      }
      prevMessageCountRef.current = messages.length;
    }
  }, [messages]);

  // Fetch files for the selected project when project changes
  useEffect(() => {
    const fetchFilesForProject = async () => {
      if (!selectedProjectId) return;
      try {
        const response = await axiosInstance.get(
          `${webAPIs.getConversation}?projectId=${selectedProjectId}`
        );
        if (response.data.success && response.data.result.files) {
          setFiles(
            response.data.result.files.map((f) => ({
              ...f,
              projectId: selectedProjectId,
            }))
          );
          // Optionally reset selectedDocIds to only those in this project
          // setSelectedDocIds([]);
        } else {
          setFiles([]);
          // setSelectedDocIds([]);
        }
      } catch (e) {
        setFiles([]);
        // setSelectedDocIds([]);
      }
    };
    fetchFilesForProject();
  }, [selectedProjectId, setFiles, setSelectedDocIds]);

  // Re-fetch conversation when navigating back to this page
  useEffect(() => {
    if (location.pathname === "/dashboard/quick-prompt") {
      fetchPreviousConversation();
    }
  }, [location.pathname, fetchPreviousConversation]);

  // Sync with global message state when Clear Chat is clicked from Sidebar
  useEffect(() => {
    if (globalMessage && location.pathname === "/dashboard/quick-prompt") {
      fetchPreviousConversation();
    }
  }, [globalMessage, location.pathname, fetchPreviousConversation]);

  const handleRemoveConversation = async (index) => {
    const updatedMessages = [...messages];
    updatedMessages.splice(index, 2);
    try {
      await axiosInstance.post(
        `${webAPIs.clearConversation}?projectId=${selectedProjectId}`,
        {
          conversation: updatedMessages,
        }
      );
      setMessages(updatedMessages);
    } catch (error) {
      console.error("Error removing conversation:", error);
    }
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    // Reset open text values when changing role
    setOpenTextValues({});
  };

  const handleOpenTextChange = (id, value) => {
    console.log(`Updating open text value for ${id}:`, value);
    setOpenTextValues((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleCreate = async () => {
    if (!selectedProjectId) {
      notification.warning({
        description: "Please select a project",
        duration: 2,
      });
      return;
    }

    if (!selectedRole) {
      notification.warning({
        message: "Please select an analysis type",
        duration: 2,
      });
      return;
    }

    const missingFields =
      selectedRole.hasOpenTextField === "yes" &&
      (!openTextValues[selectedRole.openTextId] ||
        openTextValues[selectedRole.openTextId].trim() === "");

    if (missingFields) {
      notification.warning({
        message: "Please fill in all required fields",
        duration: 2,
      });
      return;
    }
    if (!files || files.length === 0) {
      notification.warning({
        description: "Please upload documents",
        duration: 2,
      });
      return;
    }

    if (!selectedDocIds || selectedDocIds.length === 0) {
      notification.warning({
        description: "Please select at least one document",
        duration: 2,
      });
      return;
    }

    setGlobalLoading(true);
    try {
      let prompt = selectedRole.userPrompt;
      if (selectedRole.hasOpenTextField === "yes") {
        // Validate that openTextId exists
        if (!selectedRole.openTextId) {
          notification.error({
            message: "Configuration Error",
            description:
              "The preset prompt is missing required configuration. Please contact support.",
            duration: 3,
          });
          setGlobalLoading(false);
          return;
        }

        const placeholder = `{${selectedRole.openTextId}}`;
        const replacementValue = openTextValues[selectedRole.openTextId] || "";

        // Debug logging
        console.log("Preset prompt replacement debug:");
        console.log("Selected role:", selectedRole);
        console.log("Original prompt:", prompt);
        console.log("Placeholder to replace:", placeholder);
        console.log("Replacement value:", replacementValue);
        console.log("Open text values:", openTextValues);

        if (replacementValue.trim() === "") {
          notification.warning({
            message: "Please fill in the required field",
            description: `The field "${selectedRole.openTextShow}" is required.`,
            duration: 3,
          });
          setGlobalLoading(false);
          return;
        }

        // Use a more robust replacement that handles multiple occurrences and different quote types
        prompt = prompt.replace(
          new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"),
          replacementValue
        );

        // Verify replacement worked
        if (prompt.includes(placeholder)) {
          console.error(
            "Replacement failed! Placeholder still exists in prompt"
          );
          notification.error({
            message: "Template Error",
            description:
              "There was an issue processing the preset prompt template. Please try again.",
            duration: 3,
          });
          setGlobalLoading(false);
          return;
        }

        console.log("Final prompt after replacement:", prompt);
      }

      // Modify adminPrompt to include selected language
      let modifiedAdminPrompt = adminPrompt;
      if (modifiedAdminPrompt.includes("Please write in")) {
        modifiedAdminPrompt = modifiedAdminPrompt.replace(
          /Please write in [a-zA-Z]+\./,
          `Please write in ${outputLanguage}.`
        );
      } else {
        modifiedAdminPrompt = `${modifiedAdminPrompt} Please write in ${outputLanguage}.`;
      }

      const newUserMessage = {
        role: "user",
        content: prompt,
      };

      const updatedMessages = [...messages, newUserMessage];
      setMessages(updatedMessages);
      const response = await axiosInstance.post(
        webAPIs.chat,
        {
          prompt: prompt,
          systemPrompt: modifiedAdminPrompt,
          checked: checked,
          temp: 0,
          documentIds: selectedDocIds,
          projectId: selectedProjectId,
        },
        {
          timeout: 1000000,
        }
      );

      const newAssistantMessage = {
        role: "assistant",
        content: response.data.result,
      };

      const finalMessages = [...updatedMessages, newAssistantMessage];
      setMessages(finalMessages);

      // Store token information for the last response
      if (response.data.tokenInfo) {
        setLastTokenInfo(response.data.tokenInfo);
      }

      // await axios.post(webAPIs.clearConversation, {
      //   user: user.id,
      //   conversation: finalMessages,
      // });
    } catch (error) {
      notification.error({
        description: error.response?.data?.message || "Server Error",
        duration: 2,
      });
    } finally {
      setGlobalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex w-full h-full justify-center items-center">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="flex h-full w-full">
      {/* Left Column - Prompt Selection */}
      <div className="w-1/3 p-5 border-r border-gray-200 flex flex-col">
        <div className="overflow-y-auto">
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">
              Quick Prompts for Role: {userRoleTitle}
            </h2>
            <Radio.Group
              onChange={(e) => handleRoleSelect(e.target.value)}
              value={selectedRole}
              className="flex flex-col gap-3 [&_.ant-radio-checked_.ant-radio-inner]:bg-[#D8AE5F] [&_.ant-radio-checked_.ant-radio-inner]:border-[#D8AE5F] [&_.ant-radio:hover_.ant-radio-inner]:border-[#D8AE5F] [&_.ant-radio-checked_.ant-radio-inner::after]:bg-white [&_.ant-radio-checked_.ant-radio-inner::after]:scale-[0.3]"
            >
              {presetPrompts.map((prompt) => (
                <Radio key={prompt.id} value={prompt}>
                  {prompt.itemText}
                </Radio>
              ))}
            </Radio.Group>
          </div>

          {selectedRole?.hasOpenTextField && selectedRole?.openTextShow && (
            <div className="mb-8">
              <Input.TextArea
                placeholder={selectedRole.openTextShow}
                value={openTextValues[selectedRole.openTextId] || ""}
                onChange={(e) =>
                  handleOpenTextChange(selectedRole.openTextId, e.target.value)
                }
                rows={4}
              />
            </div>
          )}
        </div>

        <div className="pt-4 border-t">
          <div className="mb-2">
            <label className="text-base font-medium">Output Language</label>
          </div>
          <Select
            className="w-full mb-4"
            value={outputLanguage}
            onChange={setOutputLanguage}
            options={[
              { label: "English (default)", value: "English" },
              { label: "Arabic", value: "Arabic" },
              { label: "Chinese", value: "Chinese" },
              { label: "French", value: "French" },
              { label: "German", value: "German" },
              { label: "Italian", value: "Italian" },
              { label: "Japanese", value: "Japanese" },
              { label: "Portuguese", value: "Portuguese" },
              { label: "Spanish", value: "Spanish" },
              { label: "Turkish", value: "Turkish" },
            ]}
          />

          <Button
            type="primary"
            className="w-full bg-blue-500 hover:bg-blue-600 flex items-center justify-center gap-2"
            onClick={handleCreate}
            icon={<SendOutlined />}
          >
            Create
          </Button>
        </div>
      </div>

      {/* Right Column - Results */}
      <div className="w-2/3 bg-mainColor flex flex-col">
        <div className="flex-1 h-[100%] text-textColor flex flex-col items-center text-lg overflow-y-auto messages-container">
          {messages.map((item, index) => (
            <div
              key={index}
              className={`w-[100%] flex justify-center py-4 ${
                item.role === "assistant" ? "bg-botColor" : "bg-userColor"
              }`}
            >
              <div className="sm:w-[96%] w-full px-2 flex items-center">
                {item.role === "user" ? (
                  <div className="flex items-center w-full">
                    <UserOutlined className="text-2xl mr-6" />
                    <ReactMarkdown
                      className="markdown-body flex-1 pt-3 text-textColor"
                      remarkPlugins={[remarkMath, remarkGfm]}
                      rehypePlugins={[rehypeKatex]}
                    >
                      {item.content}
                    </ReactMarkdown>
                    <DeleteOutlined
                      onClick={() => handleRemoveConversation(index)}
                    />
                  </div>
                ) : (
                  <>
                    <RobotOutlined className="text-2xl mr-6 text-textColor" />
                    <ReactMarkdown
                      className="markdown-body flex-1 pt-3 text-textColor"
                      remarkPlugins={[remarkMath, remarkGfm]}
                      rehypePlugins={[rehypeKatex]}
                    >
                      {item.content}
                    </ReactMarkdown>
                  </>
                )}
              </div>
            </div>
          ))}
          {messages.length === 0 && (
            <div className="text-gray-400 text-center mt-4">
              Results will appear here after analysis
            </div>
          )}
        </div>

        {/* Token Usage Indicator */}
        {lastTokenInfo && (
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
            <TokenUsageIndicator tokenInfo={lastTokenInfo} />
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickPromptPage;
